import { ref } from 'vue'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

/**
 * 공지사항 관리 Composable
 *
 * Firestore Collection: notices
 * {
 *   title: string,           // 제목
 *   content: string,         // 내용 (HTML 지원)
 *   isImportant: boolean,    // 중요 공지 여부
 *   isActive: boolean,       // 노출 여부
 *   viewCount: number,       // 조회수
 *   createdAt: string,       // 생성일
 *   updatedAt: string        // 수정일
 * }
 */
export function useNotice() {
  const loading = ref(false)
  const error = ref(null)

  // ==========================================
  // Admin Functions
  // ==========================================

  /**
   * 모든 공지사항 조회 (Admin용)
   */
  const getAllNotices = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'notices'),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('공지사항 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * 공지사항 생성 (Admin용)
   */
  const createNotice = async (noticeData) => {
    loading.value = true
    error.value = null

    try {
      const now = new Date().toISOString()

      const docRef = await addDoc(collection(db, 'notices'), {
        title: noticeData.title,
        content: noticeData.content,
        isImportant: noticeData.isImportant || false,
        isActive: true,
        viewCount: 0,
        createdAt: now,
        updatedAt: now
      })

      return docRef.id
    } catch (err) {
      console.error('공지사항 생성 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 공지사항 수정 (Admin용)
   */
  const updateNotice = async (noticeId, noticeData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'notices', noticeId)

      await updateDoc(docRef, {
        title: noticeData.title,
        content: noticeData.content,
        isImportant: noticeData.isImportant || false,
        updatedAt: new Date().toISOString()
      })

      return true
    } catch (err) {
      console.error('공지사항 수정 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 공지사항 활성화/비활성화 토글 (Admin용)
   */
  const toggleNoticeActive = async (noticeId, isActive) => {
    try {
      const docRef = doc(db, 'notices', noticeId)

      await updateDoc(docRef, {
        isActive,
        updatedAt: new Date().toISOString()
      })

      return true
    } catch (err) {
      console.error('공지사항 상태 변경 실패:', err)
      error.value = err.message
      throw err
    }
  }

  /**
   * 공지사항 삭제 (Admin용)
   */
  const deleteNotice = async (noticeId) => {
    loading.value = true
    error.value = null

    try {
      await deleteDoc(doc(db, 'notices', noticeId))
      return true
    } catch (err) {
      console.error('공지사항 삭제 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  // ==========================================
  // User Functions
  // ==========================================

  /**
   * 활성화된 공지사항 목록 조회 (User용)
   * @param {number} pageSize - 페이지당 개수
   * @param {DocumentSnapshot} lastDoc - 페이지네이션용 마지막 문서
   */
  const getActiveNotices = async (pageSize = 20, lastDoc = null) => {
    loading.value = true
    error.value = null

    try {
      let q = query(
        collection(db, 'notices'),
        where('isActive', '==', true),
        orderBy('isImportant', 'desc'),
        orderBy('createdAt', 'desc'),
        limit(pageSize)
      )

      if (lastDoc) {
        q = query(
          collection(db, 'notices'),
          where('isActive', '==', true),
          orderBy('isImportant', 'desc'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(pageSize)
        )
      }

      const snapshot = await getDocs(q)
      const notices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null
      const hasMore = snapshot.docs.length === pageSize

      return { notices, lastVisible, hasMore }
    } catch (err) {
      console.error('공지사항 목록 조회 실패:', err)
      error.value = err.message
      return { notices: [], lastVisible: null, hasMore: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * 공지사항 상세 조회 (User용)
   * 조회수도 함께 증가
   */
  const getNoticeById = async (noticeId, incrementView = true) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'notices', noticeId)
      const docSnap = await getDoc(docRef)

      if (!docSnap.exists()) {
        throw new Error('공지사항을 찾을 수 없습니다.')
      }

      const notice = {
        id: docSnap.id,
        ...docSnap.data()
      }

      // 조회수 증가 (비동기로 처리, 에러 무시)
      if (incrementView) {
        updateDoc(docRef, {
          viewCount: (notice.viewCount || 0) + 1
        }).catch(() => {})
      }

      return notice
    } catch (err) {
      console.error('공지사항 조회 실패:', err)
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * 중요 공지사항만 조회 (홈 화면 등에서 사용)
   */
  const getImportantNotices = async (limitCount = 5) => {
    try {
      const q = query(
        collection(db, 'notices'),
        where('isActive', '==', true),
        where('isImportant', '==', true),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )

      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('중요 공지사항 조회 실패:', err)
      return []
    }
  }

  /**
   * 최근 공지사항 조회 (알림 아이콘 뱃지 등에서 사용)
   * @param {number} days - 최근 N일 이내
   */
  const getRecentNoticesCount = async (days = 7) => {
    try {
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)

      const q = query(
        collection(db, 'notices'),
        where('isActive', '==', true),
        where('createdAt', '>=', cutoffDate.toISOString())
      )

      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (err) {
      console.error('최근 공지사항 개수 조회 실패:', err)
      return 0
    }
  }

  return {
    loading,
    error,
    // Admin
    getAllNotices,
    createNotice,
    updateNotice,
    toggleNoticeActive,
    deleteNotice,
    // User
    getActiveNotices,
    getNoticeById,
    getImportantNotices,
    getRecentNoticesCount
  }
}
